import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./AddFormModule.css";

const FormModule = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/form-modules/`);
      setModules(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching form modules:", error);
      Swal.fire("Error", "Failed to load form modules", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This form module will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/form-modules/${id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Form module has been removed.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchModules();
    } catch (error) {
      console.error("Error deleting form module:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete form module",
        "error"
      );
    }
  };

  const handleEdit = (item) => {
    navigate(`/catalog/edit-form-module/${item.id}`);
  };

  const statusClass = (status) => {
    if (status === "PUBLISHED") return "active";
    if (status === "RETIRED") return "inactive";
    return "draft";
  };

  const filteredModules = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    return modules.filter(
      (item) =>
        item.module_name?.toLowerCase().includes(search) ||
        item.module_code?.toLowerCase().includes(search)
    );
  }, [modules, searchTerm]);

  const totalPages = Math.ceil(filteredModules.length / entriesPerPage);

  const paginatedModules = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    return filteredModules.slice(startIndex, startIndex + entriesPerPage);
  }, [filteredModules, currentPage, entriesPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startEntry =
    filteredModules.length === 0
      ? 0
      : (currentPage - 1) * entriesPerPage + 1;

  const endEntry = Math.min(
    currentPage * entriesPerPage,
    filteredModules.length
  );

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, "...", totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="form-module-page-content">
          <div className="form-module-table-header">
            <div className="form-module-header-left">
              <h2>
                Form Modules Management
              </h2>

              <span className="form-module-count">
                {modules.length} modules
              </span>
            </div>

            <div className="form-module-header-right">
              <div className="form-module-search-wrapper">
                <FiSearch className="form-module-search-icon" />

                <input
                  type="text"
                  placeholder="Search modules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-module-search-input"
                />
              </div>

              <button
                className="form-module-refresh-btn"
                onClick={fetchModules}
                title="Refresh"
              >
                <FiRefreshCw />
              </button>

              <button
                className="form-module-add-btn"
                onClick={() => navigate("/catalog/add-form-module")}
              >
                <FiPlus />
                Add Module
              </button>
            </div>
          </div>

          {loading ? (
            <div className="form-module-loader-container">
              <div className="form-module-loader">
                <div className="form-module-spinner"></div>
                <p>Loading form modules...</p>
              </div>
            </div>
          ) : (
            <div className="form-module-table-container">
              <table className="form-module-table">
                <thead>
                  <tr>
                    <th>Module Name</th>
                    <th>Module Code</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th className="form-module-actions-header">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedModules.length > 0 ? (
                    paginatedModules.map((item) => (
                      <tr key={item.id}>
                        <td className="form-module-name-cell">
                          <div className="form-module-name-wrapper">
                            <div className="form-module-avatar">
                              {item.module_name?.charAt(0).toUpperCase()}
                            </div>

                            <span>{item.module_name}</span>
                          </div>
                        </td>

                        <td>
                          <span className="form-module-code-badge">
                            {item.module_code}
                          </span>
                        </td>

                        <td>{item.description || "-"}</td>

                        <td>
                          <span
                            className={`form-module-status ${statusClass(
                              item.status
                            )}`}
                          >
                            <span className="form-module-status-dot"></span>
                            {item.status}
                          </span>
                        </td>

                        <td className="form-module-actions-cell">
                          <button
                            className="form-module-action-btn form-module-edit-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            className="form-module-action-btn form-module-delete-btn"
                            onClick={() => handleDelete(item.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="form-module-no-data">
                        <div className="form-module-empty-state">
                          <span className="form-module-empty-icon">📋</span>

                          <p>No form modules found</p>

                          <span className="form-module-empty-sub">
                            {searchTerm
                              ? "Try adjusting your search"
                              : "Add your first form module to get started"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {filteredModules.length > 0 && (
                <div className="form-module-pagination">
                  <div className="form-module-show-entries">
                    <span>Show</span>

                    <select
                      value={entriesPerPage}
                      onChange={(e) => {
                        setEntriesPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>

                    <span>entries</span>
                  </div>

                  <div className="form-module-pagination-info">
                    Showing <strong>{startEntry}</strong> to{" "}
                    <strong>{endEntry}</strong> of{" "}
                    <strong>{filteredModules.length}</strong> entries
                  </div>

                  <div className="form-module-pagination-controls">
                    <button
                      className="form-module-page-btn form-module-prev-btn"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      title="Previous"
                    >
                      <FiChevronLeft />
                    </button>

                    <div className="form-module-page-numbers">
                      {getPageNumbers().map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="form-module-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`form-module-page-btn ${
                              currentPage === page
                                ? "form-module-page-active"
                                : ""
                            }`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="form-module-page-btn form-module-next-btn"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, totalPages)
                        )
                      }
                      disabled={
                        currentPage === totalPages || totalPages === 0
                      }
                      title="Next"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormModule;